import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, CircularProgress, Alert, TextField,
  ToggleButton, ToggleButtonGroup, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { httpClient } from '../services/http-client';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

interface ActivityItem {
  date: string;
  description: string;
  account: string;
  account_id: string;
  institution: string;
  amount: string;
  currency: string;
  source: string;
  kind: string;
}

const WINDOWS = [
  { months: 1, label: '1 month' },
  { months: 3, label: '3 months' },
  { months: 12, label: '12 months' },
];

/**
 * Everything that moved, newest first.
 *
 * Sourced from the double-entry ledger rather than the `snapshots` table.
 * Snapshots are written by hand; every importer posts journal entries, so a
 * snapshot-backed feed shows nothing for a household whose data arrived
 * through Plaid or a CSV — which is all of them.
 *
 * One row per movement, not two. A transfer posts a debit and a credit;
 * listing both would read as two separate events for one payment.
 */
export const ActivityFeedPage: React.FC = () => {
  const baseCurrency = useBaseCurrency();
  const [months, setMonths] = useState(1);
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    httpClient
      .get<{ items: ActivityItem[] }>(`/activity/recent?months=${months}&limit=500`)
      .then((r) => {
        if (cancelled) return;
        const list = ((r as any)?.data?.items ?? (r as any)?.items ?? []) as ActivityItem[];
        setItems(Array.isArray(list) ? list : []);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message ?? 'Could not load activity'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [months]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      i.description.toLowerCase().includes(q) ||
      i.account.toLowerCase().includes(q) ||
      (i.institution ?? '').toLowerCase().includes(q));
  }, [items, query]);

  const total = useMemo(
    () => filtered.reduce((s, i) => s + Math.abs(Number(i.amount || 0)), 0),
    [filtered],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Activity</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every movement across your accounts, drawn from the ledger.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          size="small" exclusive value={months}
          onChange={(_, v) => v && setMonths(v)}
        >
          {WINDOWS.map((w) => (
            <ToggleButton key={w.months} value={w.months}>{w.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Filter by payee, account or bank"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />

        <Box sx={{ flex: 1 }} />
        <Chip label={`${filtered.length} events`} size="small" />
        {/* Magnitude, not a net figure: this mixes inflows and outflows across
            currencies, and a "total" implying otherwise would be misleading. */}
        <Chip
          size="small" variant="outlined"
          label={`${formatCurrency(total, baseCurrency as any, { base: baseCurrency as any })} moved`}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">
          {items.length === 0
            ? 'No activity in this period. Connect an account or import a statement.'
            : 'Nothing matches that filter.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((i, idx) => (
                <TableRow key={`${i.account_id}-${i.date}-${idx}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(i.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{i.description}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{i.account}</Typography>
                    {i.institution && (
                      <Typography variant="caption" color="text.secondary">
                        {i.institution}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(Math.abs(Number(i.amount)), i.currency as any,
                      { base: baseCurrency as any })}
                  </TableCell>
                  <TableCell>
                    <Chip label={i.source} size="small" variant="outlined" sx={{ height: 20 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
